import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import emailToName from '../utils/emailToName';
import { useNavigate } from 'react-router-dom';
import cachedApi from '../utils/cachedApi';
import { socket } from '../socket';

// 상태 옵션
const STATUS_OPTIONS = ['준비중', '진행중', '완료'];

// 회사 일정 등록/수정 모달
const ScheduleEditModal = ({ open, onClose, onSave, defaultValue }: {
  open: boolean,
  onClose: () => void,
  onSave: (data: any) => void,
  defaultValue?: any
}) => {
  const [title, setTitle] = useState(defaultValue?.title || '');
  const [description, setDescription] = useState(defaultValue?.description || '');
  const [status, setStatus] = useState(defaultValue?.status || '준비중');
  const [dueDate, setDueDate] = useState(defaultValue?.dueDate || '');
  const [checklist, setChecklist] = useState(defaultValue?.checklist || [{ text: '', checked: false }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTitle(defaultValue?.title || '');
    setDescription(defaultValue?.description || '');
    setStatus(defaultValue?.status || '준비중');
    setDueDate(defaultValue?.dueDate || '');
    setChecklist(defaultValue?.checklist || [{ text: '', checked: false }]);
  }, [defaultValue, open]);

  const handleChecklistChange = (idx: number, value: string) => {
    setChecklist(cl => cl.map((item, i) => i === idx ? { ...item, text: value } : item));
  };
  const handleChecklistCheck = (idx: number, checked: boolean) => {
    setChecklist(cl => cl.map((item, i) => i === idx ? { ...item, checked } : item));
  };
  const handleAddChecklist = () => {
    setChecklist(cl => [...cl, { text: '', checked: false }]);
  };
  const handleRemoveChecklist = (idx: number) => {
    setChecklist(cl => cl.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim() || !dueDate.trim()) return;
    setLoading(true);
    await onSave({ title, description, status, dueDate, checklist });
    setLoading(false);
    onClose();
  };
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 400, maxWidth: 600, minHeight: 400, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '15%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#1976d2' }}>{defaultValue ? '일정 수정' : '일정 등록'}</div>
        <div style={{ marginBottom: 12 }}>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="제목" style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 4, border: '1px solid #bbb', marginBottom: 8 }} />
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="설명" style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 4, border: '1px solid #bbb', minHeight: 80, marginBottom: 8 }} />
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, marginRight: 8 }}>상태:</span>
            {STATUS_OPTIONS.map(opt => (
              <label key={opt} style={{ marginRight: 12 }}>
                <input type="radio" checked={status === opt} onChange={() => setStatus(opt)} /> {opt}
              </label>
            ))}
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, marginRight: 8 }}>마감일 or 시행일:</span>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>준비항목:</span>
            {checklist.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <input type="checkbox" checked={item.checked} onChange={e => handleChecklistCheck(idx, e.target.checked)} style={{ marginRight: 6 }} />
                <input type="text" value={item.text} onChange={e => handleChecklistChange(idx, e.target.value)} placeholder="항목 입력" style={{ flex: 1, fontSize: 15, padding: 4, borderRadius: 4, border: '1px solid #bbb', marginRight: 4 }} />
                {checklist.length > 1 && <button onClick={() => handleRemoveChecklist(idx)} style={{ color: '#d32f2f', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>}
              </div>
            ))}
            <button onClick={handleAddChecklist} style={{ background: '#eee', color: '#1976d2', border: 'none', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginTop: 4 }}>+ 항목 추가</button>
          </div>
        </div>
        <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 22px', fontWeight: 600, fontSize: 16, marginTop: 8, cursor: 'pointer' }} onClick={handleSubmit} disabled={loading}>{loading ? '저장중...' : '저장'}</button>
      </div>
    </div>
  );
};

// 상세 모달
const ScheduleDetailModal = ({ open, onClose, schedule, onEdit }: {
  open: boolean,
  onClose: () => void,
  schedule: any,
  onEdit: () => void
}) => {
  if (!open || !schedule) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10001 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 400, maxWidth: 600, minHeight: 400, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '15%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 10, color: '#1976d2', borderBottom: '1px solid #eee', paddingBottom: 8 }}>{schedule.title}</div>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontWeight: 600, color: '#555' }}>[상태]</span> {schedule.status} &nbsp;&nbsp;
          <span style={{ fontWeight: 600, color: '#555' }}>[마감일 or 시행일]</span> {schedule.dueDate}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#555' }}>[설명]</div>
          <div style={{ whiteSpace: 'pre-line', color: '#333', marginBottom: 8 }}>{schedule.description}</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#555' }}>[준비사항]</div>
          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
            {schedule.checklist?.map((item: any, idx: number) => (
              <li key={idx} style={{ fontSize: 15, color: item.checked ? '#1976d2' : '#888', marginBottom: 2 }}>
                <input type="checkbox" checked={item.checked} readOnly style={{ marginRight: 6 }} />
                {item.text}
              </li>
            ))}
          </ul>
        </div>
        <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600, fontSize: 15, marginRight: 8, cursor: 'pointer' }} onClick={onEdit}>수정</button>
        <button style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }} onClick={onClose}>닫기</button>
      </div>
    </div>
  );
};

// 전체보기 모달
const ScheduleListModal = ({ open, onClose, schedules, onEdit, onDelete, onRegister, onDetail }: {
  open: boolean,
  onClose: () => void,
  schedules: any[],
  onEdit: (schedule: any) => void,
  onDelete: (id: string) => void,
  onRegister: () => void,
  onDetail: (schedule: any) => void,
}) => {
  const user = auth.currentUser;
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 9999 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 600, maxWidth: 900, minHeight: 500, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '10%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 18, color: '#1976d2' }}>전체 회사 주요 일정</div>
        <button onClick={onRegister} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600, fontSize: 15, marginBottom: 18, cursor: 'pointer' }}>회사 주요일정 등록</button>
        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
          {schedules.length === 0 ? (
            <div style={{ color: '#888', textAlign: 'center', marginTop: 40 }}>등록된 일정이 없습니다.</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {schedules.map(s => {
                const isMine = user && s.createdBy === user.email;
                return (
                  <li key={s.id} style={{ borderBottom: '1px solid #eee', padding: '12px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, cursor: 'pointer' }} onClick={() => onDetail(s)}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            fontSize: 15,
                            marginRight: 2,
                            color: s.status === '준비중' ? '#888' : s.status === '진행중' ? '#1976d2' : '#43a047',
                            fontWeight: 700
                          }}
                        >
                          [{s.status}]
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 18, color: '#1976d2' }}>{s.title}</span>
                      </div>
                      <div style={{ color: '#444', fontSize: 16, margin: '8px 0 4px 0', lineHeight: 1.7, whiteSpace: 'pre-line', wordBreak: 'break-all' }}>{s.description}</div>
                      {Array.isArray(s.checklist) && s.checklist.length > 0 && (
                        <div style={{ margin: '6px 0 0 0' }}>
                          <div style={{ fontWeight: 600, color: '#555', fontSize: 14, marginBottom: 2 }}>[준비사항]</div>
                          <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0 }}>
                            {s.checklist.map((item, idx) => (
                              <li key={idx} style={{ fontSize: 15, color: item.checked ? '#1976d2' : '#888', marginBottom: 2, display: 'flex', alignItems: 'center' }}>
                                <input type="checkbox" checked={item.checked} readOnly style={{ marginRight: 6 }} />
                                {item.text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div style={{ color: '#bbb', fontSize: 12, textAlign: 'right' }}>마감일 or 시행일 : {s.dueDate} / {emailToName[s.createdBy] || s.createdBy}</div>
                    </div>
                    {isMine && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button style={{ background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onEdit(s); }}>수정</button>
                        <button style={{ background: '#fff', color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onDelete(s.id); }}>삭제</button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const CompanyScheduleWidget = () => {
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailTarget, setDetailTarget] = useState<any>(null);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  // 일정 불러오기
  const fetchSchedules = async () => {
    setLoading(true);
    try {
      // 캐시된 API 사용
      // const data = await cachedApi.getCompanySchedules();
      // setSchedules(data);
      // ↓ 항상 Firestore에서 직접 조회
      const q = query(collection(db, 'companySchedules'), orderBy('dueDate', 'asc'));
      const snap = await getDocs(q);
      setSchedules(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('회사 일정 조회 실패:', error);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchSchedules(); }, []);

  // 등록/수정
  const handleSave = async (data: any) => {
    const user = auth.currentUser;
    if (editTarget) {
      await updateDoc(doc(db, 'companySchedules', editTarget.id), {
        ...data,
        updatedAt: serverTimestamp(),
      });
      
      // 소켓 이벤트 발생 (수정)
      socket.emit('company-schedule-updated', {
        id: editTarget.id,
        ...data,
        authorEmail: user?.email || ''
      });
    } else {
      const docRef = await addDoc(collection(db, 'companySchedules'), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.email || '',
      });
      
      // 소켓 이벤트 발생 (등록)
      socket.emit('company-schedule-added', {
        id: docRef.id,
        ...data,
        authorEmail: user?.email || ''
      });
    }
    setShowEdit(false);
    setEditTarget(null);
    await fetchSchedules();
  };

  // 삭제
  const handleDelete = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'companySchedules', id));
    
    // 소켓 이벤트 발생
    socket.emit('company-schedule-deleted', {
      id,
      authorEmail: auth.currentUser?.email || ''
    });
    
    await fetchSchedules();
  };

  // 상세 열기
  const handleDetail = (schedule: any) => {
    setDetailTarget(schedule);
    setShowDetail(true);
  };

  // 수정 열기
  const handleEdit = (schedule: any) => {
    setEditTarget(schedule);
    setShowEdit(true);
    setShowDetail(false);
  };

  // 전체보기 열기
  const handleOpenAll = () => {
    navigate('/company-schedule');
  };

  // 등록 버튼(전체보기에서 사용)
  const handleRegister = () => {
    setEditTarget(null);
    setShowEdit(true);
    setShowAll(false);
  };

  const user = auth.currentUser;
  // 메인 카드에는 3개만 노출
  const mainSchedules = schedules.slice(0, 3);

  return (
    <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.10)', padding: '28px 32px 18px 32px', width: '100%', maxWidth: '100%', minWidth: 0, margin: '32px 0 0 0', minHeight: 220 }}>
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 22, color: '#444444' }}>회사 주요 일정</div>
        <button style={{ background: '#eee', color: '#1976d2', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }} onClick={handleOpenAll}>전체보기</button>
      </div>
      <div style={{ borderBottom: '1px solid #eee', margin: '8px 0 16px 0' }} />
      {loading ? <div style={{ color: '#888', textAlign: 'center', margin: 30 }}>불러오는 중...</div> : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
          {mainSchedules.length === 0 ? <div style={{ color: '#888', textAlign: 'center', margin: 30 }}>등록된 일정이 없습니다.</div> : (
            mainSchedules.map(s => {
              const isMine = user && s.createdBy === user.email;
              return (
                <li key={s.id} style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', borderBottom: '1px solid #f3f3f3', padding: '8px 0' }} onClick={() => handleDetail(s)}>
                  <span
                    style={{
                      fontSize: 15,
                      marginRight: 6,
                      color: s.status === '준비중' ? '#888' : s.status === '진행중' ? '#1976d2' : '#43a047',
                      fontWeight: 700
                    }}
                  >
                    [{s.status}]
                  </span>
                  <span style={{ fontWeight: 600, color: '#222', flex: 1 }}>{s.title}</span>
                  <span style={{ color: '#888', fontSize: 15 }}>{s.dueDate}</span>
                  {isMine && (
                    <>
                      <button onClick={e => { e.stopPropagation(); handleEdit(s); }} style={{ background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginLeft: 8 }}>수정</button>
                      <button onClick={e => { e.stopPropagation(); handleDelete(s.id); }} style={{ background: '#fff', color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>삭제</button>
                    </>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}
      <ScheduleEditModal open={showEdit} onClose={() => { setShowEdit(false); setEditTarget(null); setShowAll(false); }} onSave={handleSave} defaultValue={editTarget} />
      <ScheduleDetailModal open={showDetail} onClose={() => setShowDetail(false)} schedule={detailTarget} onEdit={() => handleEdit(detailTarget)} />
      <ScheduleListModal open={showAll} onClose={() => setShowAll(false)} schedules={schedules} onEdit={handleEdit} onDelete={handleDelete} onRegister={handleRegister} onDetail={handleDetail} />
    </div>
  );
};

export default CompanyScheduleWidget; 